import DirectApi from '../../../directApi';
import {
	ExportInvoiceOnSuccess,
	ExportXeroInvoiceParams,
	Invoice,
	InvoiceBillTo,
	XeroAccountItem,
} from './ExportInvoiceModal.type';
import {XeroExportTarget} from './ExportInvoiceModal.target';
import Util from '../../../forecast-app/shared/util/util';
import {changedUnitPrices, toPreFlagAccountMap, toXeroAccountMap} from './ExportInvoiceModal.xeroLines';
import CreateXeroInvoiceMutation from '../../../mutations/CreateXeroInvoiceMutation';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {IntlShape} from 'react-intl';

const getXeroAccounts = async (projectBillTo: InvoiceBillTo | undefined): Promise<XeroAccountItem[]> => {
	const {billFrom} = projectBillTo || {};
	const {externalId} = billFrom || {};

	return DirectApi.Fetch_Raw(`xero/accounts/${externalId}`).then(async result => {
		if (result.status !== 403) {
			const json = await result.json();
			return json.map(account => ({
				value: account.AccountID,
				label: account.Name,
				taxRate: account.DefaultTaxRate,
				type: account.Type,
				code: account.Code,
			}));
		}

		return [];
	});
};

export const fetchXeroAccounts = async (
	projectBillTo: InvoiceBillTo | undefined,
	setAccounts: (accounts: XeroAccountItem[]) => void
): Promise<void> => {
	const accounts = await getXeroAccounts(projectBillTo);
	setAccounts(accounts);
};

const onExportXeroInvoiceSuccess = (
	result,
	intl: IntlShape,
	onSuccess: ExportInvoiceOnSuccess,
	onExportFailed: (reason?: string | null) => void
): void => {
	const {formatMessage} = intl;
	const {createXeroInvoice} = result;

	/*
	 * The modal closes only when the export SUCCEEDED. It used to close either way, because
	 * `onSuccess()` ran before this check - so a Xero refusal threw away every item, account and
	 * tracking pick the user had made, with no way back but making them all again.
	 */
	if (createXeroInvoice.success) {
		onSuccess();
		createToast({duration: 5000, message: formatMessage({id: 'integrations.xero.invoice_created'})});
	} else {
		/*
		 * The reason goes INTO the modal, not into a MODAL_TYPE.WARNING beside it.
		 *
		 * This modal is a design-system `Modal`: Radix portals it to document.body at z-index 1000,
		 * sets `pointerEvents: none` on the body and `aria-hidden`s the rest of the tree. The legacy
		 * warning renders inside that tree at z-index 900, so it is covered, unclickable and hidden
		 * from screen readers - the user sees the spinner stop and nothing else. That was fine while
		 * the modal closed first; it stopped being fine when this branch kept it open to preserve the
		 * user's picks. `onError` in ExportInvoiceModal.tsx has carried a comment about this hazard
		 * all along.
		 *
		 * The message this most matters for is the xero service's post-acceptance 500 - "created in
		 * Xero, but Forecast could not be fully updated. Do not export it again" - which that service
		 * returns as a 500 precisely so the user would see it.
		 */
		onExportFailed(createXeroInvoice.errorMessage);
	}
};

/**
 * The createXeroInvoice input for where the export goes.
 *
 * The legacy window's client sends exactly what the old Xero window sent - the Forecast client and
 * the organisation its contact belongs to, which the xero service resolves to the contact - and no
 * bill-to fields. The pathways bill-to sends the bill-to, its contact and its bill-from organisation.
 */
export const buildXeroInvoiceInput = ({
	target,
	invoice,
	accountMap,
	projectInDescription,
}: {
	target: XeroExportTarget;
	invoice: Pick<Invoice, 'id'>;
	accountMap: unknown[];
	projectInDescription: boolean;
}) => {
	if (target.kind === 'CLIENT') {
		return {
			invoiceId: invoice.id,
			forecastClientId: target.clientId,
			accountMap,
			tenantId: target.tenantId,
			projectInDescription,
		};
	}
	const {billTo} = target;
	return {
		invoiceId: invoice.id,
		billToId: billTo.id,
		xeroClientId: billTo?.externalId,
		accountMap,
		tenantId: billTo.billFrom?.externalId,
		projectInDescription,
	};
};

export const onExportXeroInvoice = ({
	intl,
	onSuccess,
	invoice,
	invoiceRows,
	target,
	projectInDescription,
	onError,
	useLinesTable,
	onExportFailed,
}: ExportXeroInvoiceParams): void => {
	/*
	 * Under the flag the line carries an item, a tax type and tracking as well as its account, and
	 * toXeroAccountMap omits each one that is absent. Off the flag this is byte-for-byte the map
	 * built before, because that helper is not reached at all.
	 *
	 * Prices changed by picking a Product/Service ride along on the map. The xero service sends each
	 * as the line's UnitAmount and writes it back to the invoice entry only once Xero has ACCEPTED,
	 * so the browser makes ONE call with one outcome: a refused export writes nothing and leaves the
	 * modal open with the user's picks intact. Off the flag no price can have changed, so the map is
	 * unchanged.
	 */
	const accountMap = useLinesTable
		? toXeroAccountMap(invoiceRows, changedUnitPrices(invoiceRows))
		: toPreFlagAccountMap(invoiceRows);

	Util.CommitMutation(
		CreateXeroInvoiceMutation,
		buildXeroInvoiceInput({target, invoice, accountMap, projectInDescription}),
		result => onExportXeroInvoiceSuccess(result, intl, onSuccess, onExportFailed),
		true,
		null,
		onError
	);
};
